/**
 * JourneySummary Component
 *
 * Displays journey progress summary with countries and metrics.
 */

import { useLocale, useTranslation, useCountryName } from '@tonnie/locale';
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CountryFlag,
  Progress,
  cn,
} from '@tonnie/ui';
import { ArrowRight, Calendar, Clock, MapPin } from '@tonnie/assets';
import * as React from 'react';
import { useJourneyInstance } from '@/core/contexts/JourneyInstanceContext';

// Helper function to calculate days on journey
const calculateDaysOnJourney = (startDate: string): number => {
  const start = new Date(startDate);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - start.getTime());
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export interface IJourneySummaryProps {
  className?: string;
  showMiles?: boolean;
  totalMiles?: number;
  nextMilestone?: number;
}

export function JourneySummary({
  className,
  showMiles = false,
  totalMiles,
  nextMilestone,
}: IJourneySummaryProps) {
  const { t } = useTranslation();
  // Dates in the reader's locale and country names from the catalogue (F34) —
  // this card said "Arrives Sep 7, 2027" and "Your Journey to Brazil" to a
  // pt-PT reader.
  const { formatDate, formatNumber, formatPercent } = useLocale();
  const countryName = useCountryName();
  const { activeInstance } = useJourneyInstance();

  const journeyProgress = React.useMemo(() => {
    if (!activeInstance) return 0;

    // Cornerstone refactor 2026-05: drive UI off lifecycle_state
    // (replaces the legacy synthetic `status` field). `'draft'` is
    // an invitation-only state — admin saved an invite but didn't
    // send it. A draft invitation has no resulting journey row, so
    // a `JourneyInstance` never carries `lifecycleState='draft'` here.
    // The reachable "not started yet" state is `'pending_setup'`.
    const { lifecycleState, createdAt } = activeInstance;
    if (lifecycleState === 'completed') return 100;
    if (lifecycleState === 'pending_setup') return 5;

    if ((lifecycleState === 'active' || lifecycleState === 'paused') && createdAt) {
      const daysElapsed = Math.max(
        0,
        Math.floor((Date.now() - new Date(createdAt).getTime()) / 86_400_000)
      );
      const typicalDuration = 180;
      return Math.min(90, Math.round((daysElapsed / typicalDuration) * 85) + 5);
    }

    return 5;
  }, [activeInstance]);

  const estimatedArrival = React.useMemo(() => {
    if (activeInstance?.paymentInfo?.expirationDate) {
      return new Date(activeInstance.paymentInfo.expirationDate).toISOString().split('T')[0];
    }
    const base = activeInstance?.createdAt ? new Date(activeInstance.createdAt) : new Date();
    base.setMonth(base.getMonth() + 6);
    return base.toISOString().split('T')[0];
  }, [activeInstance]);

  const startDate = activeInstance?.createdAt
    ? new Date(activeInstance.createdAt).toISOString().split('T')[0]
    : new Date().toISOString().split('T')[0];

  // W2 §9.5 — the placeholder treatment JourneyInstanceSelector already uses:
  // a pending_setup citizen has no origin yet, and `|| 'BR'` / `|| 'PT'` showed
  // them a route they never chose rather than admitting the field is empty.
  // W2 §9.5 — the placeholder treatment JourneyInstanceSelector already uses.
  // A pending_setup citizen has no origin yet, and 'UNK' is the platform's
  // sentinel for a destination not chosen; `|| 'BR'` / `|| 'PT'` showed both as
  // a route the citizen never picked.
  const placeholder = (c: string | null | undefined) => (!c || c === 'UNK' ? '?' : c);
  const originCountry = placeholder(activeInstance?.originCountry);
  const destinationCountry = placeholder(activeInstance?.destinationCountry);
  // Map lifecycle_state to the status badge label the existing UI
  // already understood (active / paused-as-limited / expired / etc.).
  const status: 'active' | 'expired' | 'limited' = (() => {
    const s = activeInstance?.lifecycleState;
    if (s === 'expired') return 'expired';
    if (s === 'paused' || s === 'pending_setup') return 'limited';
    return 'active';
  })();

  const daysOnJourney = calculateDaysOnJourney(startDate);
  const hasMilesData = showMiles && totalMiles != null && nextMilestone != null;
  const remainingMiles = hasMilesData ? Math.max(0, nextMilestone - totalMiles) : 0;
  const progressToMilestone = hasMilesData ? Math.min(100, (totalMiles / nextMilestone) * 100) : 0;

  const journeyTitle = activeInstance
    ? t('journey.yourJourneyTo', { country: countryName(destinationCountry) })
    : // Was the literal 'Portugal' — English prose interpolated into a
      // translated string, so every locale read "Il tuo percorso verso
      // Portugal". With no instance there is no destination to name.
      t('journey.yourJourneyToUnknown');

  return (
    <Card className={cn('border-border/50 shadow-sm', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <CountryFlag code={originCountry} size="md" />
              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
              <CountryFlag code={destinationCountry} size="md" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold">{journeyTitle}</CardTitle>
              <CardDescription className="text-xs mt-0.5 flex items-center gap-1.5">
                <Calendar className="w-3 h-3" />
                <span>{t('journey.arrives', { date: formatDate(estimatedArrival) })}</span>
              </CardDescription>
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            {t(`journey.status.${status}`)}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="pt-0 space-y-4">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">{t('journey.progress')}</span>
            <span className="font-semibold text-foreground">{formatPercent(journeyProgress)}</span>
          </div>
          <div className="relative">
            <Progress aria-label={t('journey.progress')} value={journeyProgress} className="h-2" />
            <div className="absolute top-0 left-0 right-0 flex justify-between -mt-0.5 pointer-events-none">
              {[25, 50, 75, 100].map((milestone) => (
                <div
                  key={milestone}
                  className={cn(
                    'w-0.5 h-3 rounded-full transition-opacity',
                    journeyProgress >= milestone ? 'bg-primary opacity-60' : 'bg-border opacity-20'
                  )}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 pt-3 border-t">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span>{t('journey.daysOnJourney')}</span>
            </div>
            <p className="text-base font-semibold">{daysOnJourney}</p>
          </div>

          {hasMilesData && (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <MapPin className="w-3 h-3" />
                <span>{t('journey.milesToGo')}</span>
              </div>
              <p className="text-base font-semibold text-primary-accessible">
                {formatNumber(remainingMiles)}
              </p>
            </div>
          )}
        </div>

        {hasMilesData && (
          <div className="pt-2 border-t">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="text-muted-foreground">{t('journey.nextMilestone')}</span>
              <span className="font-medium text-primary-accessible">
                {formatPercent(progressToMilestone)}
              </span>
            </div>
            <Progress
              aria-label={t('journey.nextMilestone')}
              value={progressToMilestone}
              className="h-1.5"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
